import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Fonts, mediaQuery } from '@styles/index';
import styled from '@emotion/styled';
import TextInput from '@cont/Internal/GenericBanner/Form/TextInput';
import Dropdown from '@comp/Dropdown/Dropdown';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Divider from '@base/Divider/Divider';
import { useToggleable } from '@stores/toggleables';
import { getIsLoggedIn } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import {
  fetchIsRegisteredUser,
  submitLotteryForm
} from '@api/fetchers/events/index';
import useKeyboardState from '@api/hooks/useKeyBoard';
import InputSearchAbleDropdown from '@comp/Dropdown/InputSearchAbleDropdown';
import { useIsMobile } from '@stores/AppCommonData';
import BounceLoader from '@comp/PlusCourseCard/BounceLoader';
import { useSnackbar } from '@stores/snackbar';
import { readCookie } from '@utils/cookies';
import { segment } from '@actions/analytics';
import UtsavThankYouCard from './UtsavThankYouCard';
import SubPointers from './SubPointers';

export interface RegisterEventData {
  thumbnail: string;
  headerLoggedIn: string;
  headerNotLoggedIn: string;
  schoolNameInputPlaceholder: string;
  fileInputPlaceholder: string;
  classInputPlaceholder: string;
  inviteCtaText: string;
  subpointsLoggedin: { image: string; displayText: string }[];
  subPointsNotLoggedIn: { image: string; displayText: string }[];
  dropdownOptions: { label: string; value: string }[];
  ctaTextLoggedIn: string;
  ctaTextNotLoggedIn: string;
  thankyouScreenData: {
    shareText: string;
    thankyouImage: string;
    lotteryImageBackground: string;
    header: string;
    description: string;
    redirectionPlaceLogo: string;
    redirectionPlaceText: string;
    redirectionPlaceLink: string;
  };
}
const HeaderTitle = styled.h2`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.H2};
  line-height: 40px;
  letter-spacing: -1%;
  color: var(--color-base-fill);
  ${mediaQuery.uptoMobile} {
    font-weight: 600;
    font-size: 20px;
    letter-spacing: 0%;
    line-height: 26px;
  }
`;
const PointText = styled.p`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.LARGE};
  line-height: 24px;
  letter-spacing: 0%;
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
    line-height: 18px;
  }
`;

const MainContainer = styled.div`
  display: flex;
  justify-content: center;
  min-height: 80vh;
  margin-bottom: -40px;
  background: var(--color-base-1);
  align-items: center;
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0px;
  }
`;
const StyledContainer = styled.div<{ $isKeyboardOpen?: boolean }>`
  max-height: 100vh;
  max-width: 100vw;
  display: flex;
  justify-content: center;
  flex-direction: row;
  ${mediaQuery.uptoMobile} {
    height: ${({ $isKeyboardOpen }) => ($isKeyboardOpen ? 'auto' : '85vh')};
    flex-direction: column;
    padding: 20px;
    padding-bottom: ${({ $isKeyboardOpen }) =>
      $isKeyboardOpen ? 'auto' : '100px'};
  }
`;
const StyledCtaText = styled.p`
  color: var(--color-base-1);
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.SMALL};
  line-height: 20px;
  letter-spacing: 0;
`;
const StyledTermsAndCondnText = styled.p`
  color: var(--color-text-primary);
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.ULTRA_SMALL};
  margin-bottom: 24px;
  line-height: 16px;
  letter-spacing: 0;
  a {
    color: var(--color-accent-blue);
    text-decoration: underline;
  }
`;
const CtaWrapper = styled.div<{ $isKeyboardOpen?: boolean }>`
  background: var(--color-base-1);
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  width: fit-content;

  ${mediaQuery.uptoMobile} {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 100;
    transition: transform 0.3s ease-in-out;
    transform: ${({ $isKeyboardOpen }) =>
      $isKeyboardOpen ? 'translateY(100%)' : 'translateY(0)'};
  }
`;
const SubmitCta = styled.div<{ disabled?: boolean; showTnc?: boolean }>`
  background: ${({ disabled }) =>
    disabled ? 'var(--color-divider)' : 'var(--color-base-fill)'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: 8px;
  justify-content: center;
  padding: 14px 140.5px;
  margin-top: 24px;
  display: flex;
  width: fit-content;
  ${mediaQuery.uptoMobile} {
    position: relative;
    bottom: 0;
    left: 0;
    width: 90%;
    padding: 14px;
    z-index: 100;
    margin-left: 16px;
    margin-right: 16px;
    margin-top: 16px;
    margin-bottom: ${({ showTnc }) => (showTnc ? '0px' : '24px')};
  }
`;
const StyledDropdownWrapper = styled(Dropdown)<{
  options: { label: string; value: string }[];
  selectedOption: string;
  onOptionClick: (item: string) => void;
  placeholder: string;
  buttonWidth: string;
  menuWidth: string;
  gridColumn: string;
  textColor: string;
}>`
  font-weight: ${Fonts.LIGHT};
  border: 1px solid var(--color-divider);
  border-radius: 8px;
  height: 48px;
  text-overflow: ellipsis;
  h6 {
    font-size: ${Fonts.SMALL};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;
const StyledImage = styled(Image)`
  border-radius: 12px;
  margin-bottom: 32px;
  margin-right: 32px;
  flex-shrink: 0;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: auto;
    margin: 0 0 16px 0;
  }
`;
const TermsAndConditions = styled.div`
  border-radius: 8px;
  justify-content: center;
  text-align: center;
  display: flex;
`;
const ContentWrapper = styled.div<{ hasInputs?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: ${({ hasInputs }) =>
    hasInputs ? 'space-between' : 'space-around'};
  ${mediaQuery.uptoMobile} {
    justify-content: space-between;
  }
`;
const InputFieldsWrapper = styled.div<{ $isKeyboardOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 328px;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${({ $isKeyboardOpen }) => ($isKeyboardOpen ? '20px' : '0')};
  }
`;
const eventMap = {
  CLICKED_ON_REGISTER: 'GTM - Clicked on Register',
  EVENT_REGISTERED: 'GTM - Event Registered'
};
const UtsavCard = ({
  data,
  utsavReferrer,
  utmSource,
  referringDomain,
  utmMedium,
  utmCampaign,
  utmContent,
  gclid
}) => {
  const { open: showSnackbar } = useSnackbar();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isMobile = useIsMobile();
  const isKeyboardOpen = useKeyboardState();
  const { myInfo } = useMyInfo();
  const isLoggedIn = getIsLoggedIn(myInfo);
  const [schoolName, setSchoolName] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [lotteryNumber, setLotteryNumber] = useState(null);
  const [activationCode, setActivationCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  useEffect(() => {
    if (isLoggedIn) {
      fetchIsRegisteredUser(readCookie('accessToken')).then((response) => {
        if (response) {
          const utsavRegistered =
            Array.isArray(response?.lotteryNumber) &&
            response?.lotteryNumber?.length > 0;
          setIsRegistered(utsavRegistered);
          if (utsavRegistered) {
            setLotteryNumber(response?.lotteryNumber?.[0]);
            if (response?.activationCode) {
              setActivationCode(response?.activationCode);
            }
          }
        }
      });
    }
  }, [isLoggedIn]);
  const onSelectedClass = (item) => {
    setSelectedClass(item);
  };
  const onSelectedGoal = (item) => {
    setSelectedGoal(item);
  };
  const isCtaDisabled =
    isLoggedIn &&
    (!schoolName ||
      !selectedClass?.value ||
      !selectedGoal?.value ||
      !selectedCity?.value);
  const onRegisterLoginClick = useCallback(() => {
    openLoginDrawer();
    segment.track(eventMap.CLICKED_ON_REGISTER, {
      gtm_event_name: 'Utsav 2025',
      referrer: utsavReferrer,
      current_page_url: window?.location?.href,
      gclid,
      referring_domain: referringDomain,
      utm_content: utmContent,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_source: utmSource
    });
  }, [
    openLoginDrawer,
    utsavReferrer,
    gclid,
    referringDomain,
    utmContent,
    utmMedium,
    utmCampaign,
    utmSource
  ]);
  const onSubmitDataClick = useCallback(async () => {
    if (
      !schoolName ||
      !selectedClass?.value ||
      !selectedGoal?.value ||
      !selectedCity?.value
    ) {
      return;
    }
    setIsLoading(true);
    const response = await submitLotteryForm({
      schoolName,
      className: selectedClass?.value,
      selectedCity: selectedCity?.value || '',
      selectedGoal: selectedGoal?.value,
      accessToken: readCookie('accessToken')
    });
    setIsLoading(false);
    if (response?.error) {
      showSnackbar({
        message: response.error.message || 'Something went wrong',
        time: 5000,
        type: 'error'
      });
    } else {
      const utsavRegistered =
        Array.isArray(response?.lotteryNumber) &&
        response?.lotteryNumber?.length > 0;
      setIsRegistered(true);
      if (utsavRegistered) {
        setLotteryNumber(response?.lotteryNumber?.[0]);
        setActivationCode(response?.activationCode);
        segment.track(eventMap.EVENT_REGISTERED, {
          gtm_event_name: 'Utsav 2025',
          lottery_id: response?.lotteryNumber?.[0],
          exam_class: selectedClass?.label,
          school_name: schoolName,
          target_exam: selectedGoal?.label,
          selected_city: selectedCity?.label,
          referrer: utsavReferrer,
          current_page_url: window?.location?.href,
          gclid,
          referring_domain: referringDomain,
          utm_content: utmContent,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          utm_source: utmSource
        });
      }
    }
  }, [
    schoolName,
    selectedClass,
    selectedGoal,
    selectedCity,
    showSnackbar,
    utsavReferrer,
    gclid,
    referringDomain,
    utmContent,
    utmMedium,
    utmCampaign,
    utmSource
  ]);
  if (isRegistered) {
    return (
      <UtsavThankYouCard
        data={data}
        activationCode={activationCode}
        lotteryNumber={lotteryNumber}
      />
    );
  }
  return (
    <MainContainer>
      <StyledContainer $isKeyboardOpen={isKeyboardOpen && isMobile}>
        <StyledImage
          src={data?.thumbnail?.url}
          alt="image"
          width={602}
          height={338}
        />
        <ContentWrapper hasInputs={isLoggedIn}>
          <div>
            <HeaderTitle>
              {isLoggedIn ? data?.headerLoggedIn : data?.headerNotLoggedIn}
            </HeaderTitle>
            <SubPointers data={data} isLoggedIn={isLoggedIn} />
            {isLoggedIn && (
              <InputFieldsWrapper $isKeyboardOpen={isKeyboardOpen && isMobile}>
                <TextInput
                  value={schoolName}
                  onChange={(value) => setSchoolName(value)}
                  placeholder={data?.schoolNameInputPlaceholder}
                  showBoldPlaceHolder
                  id="school name"
                  style={{
                    width: '328px',
                    height: '48px',
                    borderRadius: '8px'
                  }}
                  hasError={false}
                />
                {data?.cityDropdownOptions && (
                  <InputSearchAbleDropdown
                    placeholder={data?.cityInputPlaceHolder}
                    options={data?.cityDropdownOptions || []}
                    selectedOption={selectedCity || {}}
                    onSelect={(selectedCity) => setSelectedCity(selectedCity)}
                    isKeyboardOpen={isKeyboardOpen && isMobile}
                  />
                )}
                <StyledDropdownWrapper
                  options={data?.classDropdownOptions || []}
                  selectedOption={selectedClass}
                  onOptionClick={onSelectedClass}
                  placeholder={data?.classInputPlaceholder}
                  buttonWidth="328px"
                  menuWidth="328px"
                  gridColumn="auto"
                  textColor="var(--color-text-secondary)"
                />
                <StyledDropdownWrapper
                  options={data?.goalDropdownOptions || []}
                  selectedOption={selectedGoal}
                  onOptionClick={onSelectedGoal}
                  placeholder={data?.goalInputPlaceHolder}
                  buttonWidth="328px"
                  menuWidth="328px"
                  gridColumn="auto"
                  textColor="var(--color-text-secondary)"
                />
              </InputFieldsWrapper>
            )}
          </div>
          <CtaWrapper $isKeyboardOpen={isKeyboardOpen && isMobile}>
            {isMobile && <Divider />}
            <SubmitCta
              onClick={isLoggedIn ? onSubmitDataClick : onRegisterLoginClick}
              disabled={isCtaDisabled}
              showTnc={data?.tncUrl && isLoggedIn}
            >
              {isLoading ? (
                <BounceLoader showWhiteLoader />
              ) : (
                <StyledCtaText>
                  {isLoggedIn
                    ? data?.ctaTextLoggedIn
                    : data?.ctaTextNotLoggedIn}
                </StyledCtaText>
              )}
            </SubmitCta>
            {isLoggedIn && data?.tncUrl && (
              <TermsAndConditions>
                <StyledTermsAndCondnText>
                  {"Please go through Utsav's "}
                  <Link href={data?.tncUrl} target="_blank">
                    Terms and conditions
                  </Link>
                </StyledTermsAndCondnText>
              </TermsAndConditions>
            )}
          </CtaWrapper>
        </ContentWrapper>
      </StyledContainer>
    </MainContainer>
  );
};
export default UtsavCard;
